import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  DevicesStore,
  FeatureService,
  RemoteServiceFeaturesLoadingStatus,
  RemoteServiceStore,
} from '@store';
import { RemoteServiceFeaturesChartSeries } from '@vi-iot/client/index';

import moment from 'moment';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { RemoteServiceService } from '../../remote-service.service';
import { UIService } from '@services';

@Component({
  selector: 'app-remote-service-features',
  templateUrl: './remote-service-features.component.html',
  styleUrls: ['./remote-service-features.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoteServiceFeaturesComponent implements OnInit, OnDestroy {
  RemoteServiceFeaturesLoadingStatus = RemoteServiceFeaturesLoadingStatus;

  dateRange = new FormGroup({
    start: new FormControl(null),
    end: new FormControl(null),
  });
  minRangepickerDate: Date;
  maxRangepickerDate: Date;
  customDateRange = false;
  gatewayRegistrationDate: Date;
  featuresChartSeries: RemoteServiceFeaturesChartSeries[] = [];
  private deviceSerial: string;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public remoteServiceService: RemoteServiceService,
    public remoteServiceStore: RemoteServiceStore,
    public featureService: FeatureService,
    public devicesStore: DevicesStore,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private uiService: UIService
  ) {}

  ngOnInit(): void {
    this.setInitialValues();

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((queryParam) => {
        this.deviceSerial = queryParam.get('deviceSerial');
        this.remoteServiceStore.loadChartsFeatures(this.deviceSerial);
      });

    this.remoteServiceStore.featuresChartSeries$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((featuresChartSeries) => {
        this.featuresChartSeries = featuresChartSeries;
      });

    this.uiService.isSideNavOpenSubject$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((nav) => {
      if (this.deviceSerial && !nav) {
        this.remoteServiceStore.loadChartsConfig();
        this.setDayRange();
      }
    });
  }

  trackBy(_index: number, series: RemoteServiceFeaturesChartSeries): string {
    return series.seriesNameKey;
  }

  setDayRange(): void {
    const startDate = this.dateRange.value.start;
    const endDate = this.dateRange.value.end;

    if (startDate && endDate) {
      const startPointMs = moment(startDate).utcOffset(0).valueOf();
      const endPointMs = moment(endDate).utcOffset(0).valueOf();

      this.remoteServiceStore.setNewChartExtremes(startPointMs, endPointMs);

      this.customDateRange = true;

      // if we dont set timeout then datepicker panel gets stuck and doesn't hide
      setTimeout(() => {
        this.remoteServiceStore.loadChartsFeatures(this.deviceSerial);
      });
    }
  }

  resetRange(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.setDefaultRange();

    setTimeout(() => {
      this.remoteServiceStore.loadChartsFeatures(this.deviceSerial);
    });
  }

  private setDefaultRange(): void {
    this.resetDateRangePicker();
    this.cdr.detectChanges();

    const daysBefore30 = moment().subtract(30, 'days').startOf('day').toDate();
    const gatewayRegistrationDateTimeDifference = moment(this.minRangepickerDate).diff(
      daysBefore30,
      'days'
    );
    const isFullRangeNotAvailable = gatewayRegistrationDateTimeDifference >= 0;

    this.dateRange.setValue({
      start: isFullRangeNotAvailable ? this.gatewayRegistrationDate : daysBefore30,
      end: this.maxRangepickerDate,
    });

    this.remoteServiceStore.setNewChartExtremes(
      isFullRangeNotAvailable ? this.gatewayRegistrationDate.getTime() : daysBefore30.getTime(),
      this.maxRangepickerDate.getTime()
    );
    this.dateRange.setErrors({ required: null });

    this.customDateRange = false;
  }

  private resetDateRangePicker(): void {
    this.dateRange.reset();

    Object.keys(this.dateRange.controls).forEach((key) => {
      this.dateRange.get(key).setErrors(null);
    });
  }

  private setInitialValues(): void {
    this.featureService
      .selectGatewayProperties()
      .pipe(take(1))
      .subscribe((properties) => {
        this.gatewayRegistrationDate = moment(properties.registeredAt).toDate();

        this.minRangepickerDate = this.gatewayRegistrationDate;
        this.maxRangepickerDate = moment().toDate();

        this.setDefaultRange();
        this.remoteServiceStore.loadChartsConfig();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
